/**
 * Tâche 1352 (Eric 2026-06-30) — Historique LISIBLE.
 *
 * L'Historique n'affichait qu'une phrase de résumé : pour savoir ce qu'une
 * révision avait changé, il fallait la RESTAURER, c'est-à-dire modifier la
 * base pour pouvoir lire. Ce panneau montre l'avant et l'après côte à côte,
 * phrase par phrase, avec le détail mot à mot déjà utilisé par les retouches
 * à la main (`lib/diffMots.js`) — la même couleur veut dire la même chose
 * partout.
 */
import React from "react";
import { CircleNotch } from "@phosphor-icons/react";
import { diffMots } from "../../lib/diffMots";

const SOURCE_LABELS = {
  whisper: ["Whisper", "Whisper"],
  merged: ["Fusionnée", "Merged"],
  fireflies: ["Fireflies", "Fireflies"],
};

const MotsDiff = ({ avant, apres, cote, testid }) => {
  const parts = diffMots(avant, apres);
  return (
    <p className="text-[12px] leading-snug whitespace-pre-wrap break-words" data-testid={testid}>
      {parts.map((p, i) => {
        if (p.type === "egal") return <span key={i} className="text-zinc-600">{p.texte}</span>;
        if (cote === "avant" && p.type === "supprime") {
          return <span key={i} className="text-red-700 line-through">{p.texte}</span>;
        }
        if (cote === "apres" && p.type === "ajoute") {
          return <span key={i} className="text-[#002FA7] font-semibold">{p.texte}</span>;
        }
        return null;
      })}
    </p>
  );
};

export const RevisionDiffPanel = ({ diff, loading, error, lang = "fr" }) => {
  const tr = (fr, en) => (lang === "fr" ? fr : en);

  if (loading) {
    return (
      <div className="mt-2 text-[11px] text-zinc-500 inline-flex items-center gap-1.5" data-testid="revision-diff-loading">
        <CircleNotch size={12} className="animate-spin" />
        {tr("Lecture des changements…", "Reading the changes…")}
      </div>
    );
  }
  if (error) {
    return (
      <p className="mt-2 text-[11px] text-red-700" data-testid="revision-diff-error">{error}</p>
    );
  }
  if (!diff) return null;

  if (!diff.changements || diff.changements.length === 0) {
    return (
      <p className="mt-2 text-[11px] text-zinc-500" data-testid="revision-diff-empty">
        {tr(
          "Aucune phrase ne diffère : cette version est identique à celle qui l'a suivie.",
          "No sentence differs: this version is identical to the one that followed it.",
        )}
      </p>
    );
  }

  return (
    <div className="mt-2 space-y-2" data-testid="revision-diff-panel">
      <p className="text-[11px] text-zinc-500">
        {diff.tronque
          ? tr(
              `${diff.changements.length} des ${diff.total_changements} phrases changées (les premières).`,
              `${diff.changements.length} of ${diff.total_changements} changed sentences (the first ones).`,
            )
          : tr(
              `${diff.total_changements} phrase(s) changée(s).`,
              `${diff.total_changements} sentence(s) changed.`,
            )}
      </p>
      {diff.changements.map((c, i) => (
        <div key={`${c.source}-${c.index}`} data-testid={`revision-diff-change-${i}`}>
          <p className="text-[10px] uppercase tracking-wide text-zinc-400 font-semibold mb-0.5">
            {(SOURCE_LABELS[c.source] || [c.source, c.source])[lang === "fr" ? 0 : 1]}
            {" · "}
            {tr(`Phrase ${c.index + 1}`, `Sentence ${c.index + 1}`)}
            {c.locuteur_avant !== c.locuteur_apres && (
              <span className="ml-1.5 normal-case text-amber-700" data-testid={`revision-diff-speaker-${i}`}>
                {c.locuteur_avant || "—"} → {c.locuteur_apres || "—"}
              </span>
            )}
          </p>
          <div className="grid md:grid-cols-2 gap-2">
            <div className="rounded border border-zinc-200 bg-zinc-50 px-2.5 py-1.5 min-w-0">
              <p className="text-[10px] uppercase tracking-wide text-zinc-400 font-semibold mb-0.5">
                {tr("Avant", "Before")}
              </p>
              <MotsDiff
                avant={c.avant}
                apres={c.apres}
                cote="avant"
                testid={`revision-diff-before-${i}`}
              />
            </div>
            <div className="rounded border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 min-w-0">
              <p className="text-[10px] uppercase tracking-wide text-emerald-700 font-semibold mb-0.5">
                {tr("Après", "After")}
              </p>
              <MotsDiff
                avant={c.avant}
                apres={c.apres}
                cote="apres"
                testid={`revision-diff-after-${i}`}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default RevisionDiffPanel;
